import { useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import logoSvg from 'assets/logo.svg';
import 'pages/booking/BookingLayout.css';
import 'pages/booking/booking.css';
import { BOOKING_ACCOUNT_BOOKINGS_PATH, BOOKING_HOME_PATH, BOOKING_LOGIN_PATH } from 'routes/paths';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import LanguageMenu from 'components/LanguageMenu';

/**
 * Sticky header with the building logo that links back to the booking home.
 */
function BookingHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const { jwt, logoUrl } = useApi();
  const { _t } = useLang();
  const homePath = !jwt && slug ? `/booking/${slug}` : BOOKING_HOME_PATH.original;
  const accountActive = location.pathname.startsWith('/booking/account');
  const loginPath = slug
    ? `${BOOKING_LOGIN_PATH.original}?slug=${encodeURIComponent(slug)}`
    : BOOKING_LOGIN_PATH.original;
  return (
    <header className="booking-header">
      <div className="booking-header-inner">
        <button className="booking-header-logo-btn" onClick={() => navigate(homePath)}>
          <img src={logoUrl ?? logoSvg} alt={_t('Logo')} height={42} />
        </button>
        <div className="booking-header-right">
          {jwt ? (
            <nav className="booking-header-nav" aria-label={_t('Booking navigation')}>
              <Link
                className={!accountActive ? 'active' : undefined}
                aria-current={!accountActive ? 'page' : undefined}
                to={BOOKING_HOME_PATH.original}
              >
                {_t('Spaces')}
              </Link>
              <Link
                className={accountActive ? 'active' : undefined}
                aria-current={accountActive ? 'page' : undefined}
                to={BOOKING_ACCOUNT_BOOKINGS_PATH.original}
              >
                {_t('My account')}
              </Link>
            </nav>
          ) : (
            <nav className="booking-header-nav booking-header-auth-nav" aria-label={_t('Booking navigation')}>
              <Link to={loginPath}>{_t('Log in')}</Link>
            </nav>
          )}
          <LanguageMenu />
        </div>
      </div>
    </header>
  );
}

/**
 * Footer with legal links and copyright.
 */
function BookingFooter() {
  const { buildingName } = useApi();
  const { _t } = useLang();
  return (
    <footer className="booking-footer">
      <div className="booking-footer-inner">
        <nav className="booking-footer-links">
          <a href="#">{_t('Terms of Use')}</a>
          <a href="https://admin.phillip.city/privacy-policy/">{_t('Privacy Policy')}</a>
          <a href="#">{_t('Cookies')}</a>
        </nav>
        <span className="booking-footer-copy">
          © {new Date().getFullYear()} {buildingName ?? _t('Booking System')}
        </span>
      </div>
    </footer>
  );
}

/**
 * Resets the page scroll position without inheriting smooth-scroll CSS.
 */
function useImmediateScrollToTop() {
  const location = useLocation();

  useLayoutEffect(() => {
    const rootScrollBehavior = document.documentElement.style.scrollBehavior;
    const bodyScrollBehavior = document.body.style.scrollBehavior;

    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    document.documentElement.style.scrollBehavior = rootScrollBehavior;
    document.body.style.scrollBehavior = bodyScrollBehavior;
  }, [location.key]);
}

/**
 * Full-page wrapper with the booking header and footer.
 */
export default function BookingLayout({ children }: { children: React.ReactNode }) {
  useImmediateScrollToTop();

  return (
    <div className="booking-root booking-layout">
      <BookingHeader />
      <main className="booking-layout-main">{children}</main>
      <BookingFooter />
    </div>
  );
}
